import React from 'react'
import { observer } from 'mobx-react'
import Button from 'components/Button'
import {
  ModalSheet,
  ModalSheetHeader,
  ModalSheetTitle,
  ModalSheetContent,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeaderDescription,
  ModalSheetContentBlock,
  ModalSheetListItem,
} from 'components/ModalSheet'
import styles from './BookkeepingCycleDetailsDialog.sass'
import BookkeepingCycleDetailsViewModel, {
  CompletedWorkItem,
} from './BookkeepingCycleDetailsViewModel'
import { formatToLongDateWithTime } from '../BookkeepingSelectCycleDialog/formatters'

interface BookkeepingCycleDetailsDialogProps {
  vm: BookkeepingCycleDetailsViewModel
}

export default observer(function BookkeepingCycleDetailsDialog({
  vm,
}: BookkeepingCycleDetailsDialogProps) {
  const completedWorkItems = vm.completedWorkItemsInCycle.map((item, index) => (
    <BookkeepingServiceItemList key={index} item={item} />
  ))

  return (
    <ModalSheet
      showing={vm.showing}
      onRequestClose={vm.close}
      dataTest="cycle-details-dialog"
    >
      <ModalSheetHeader withCloseButton />
      <ModalSheetContent>
        <ModalSheetContentBlock>
          <ModalSheetTitle emphasized large dataTest="cycle-details-title">
            Bookkeeping cycle {vm.selectedCycleNumber} details
          </ModalSheetTitle>
          <ModalSheetHeaderDescription>
            Review details and service items included in the selected cycle.
          </ModalSheetHeaderDescription>
        </ModalSheetContentBlock>
      </ModalSheetContent>
      <ModalSheetContent>
        <BookkeepingCycleDetailItem
          label="Status"
          value={vm.cycle.cycleStatusName}
          dataTest="cycle-details-status"
        />
        <BookkeepingCycleDetailItem
          label="Date started"
          value={formatToLongDateWithTime(vm.cycle.startedAt)}
          dataTest="cycle-details-date-started"
        />
        {vm.cycle.correction?.correctedAt && (
          <BookkeepingCycleDetailItem
            label="Date corrected"
            value={formatToLongDateWithTime(vm.cycle.correction?.correctedAt)}
          />
        )}
        {vm.cycle.completedAt && (
          <BookkeepingCycleDetailItem
            label="Date completed"
            value={formatToLongDateWithTime(vm.cycle.completedAt)}
            dataTest="cycle-details-date-completed"
          />
        )}
        {vm.cycle.isCompleted && (
          <BookkeepingCycleDetailItem
            label="Service items"
            value={completedWorkItems}
            dataTest="cycle-details-service-items"
          />
        )}
      </ModalSheetContent>
      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button
            type="default"
            onClick={vm.close}
            dataTest="cycle-details-close-button"
          >
            Close
          </Button>
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
})

interface BookkeepingCycleDetailItemProps {
  label: string
  value: string | React.ReactNode
  dataTest?: string
}

const BookkeepingCycleDetailItem = ({
  label,
  value,
  dataTest,
}: BookkeepingCycleDetailItemProps) => {
  return (
    <ModalSheetListItem>
      <div className={styles.cycleDetailItemContainer} data-test={dataTest}>
        <div>{label}</div>
        <div className={styles.cycleDetailItemValue}>{value}</div>
      </div>
    </ModalSheetListItem>
  )
}

interface BookkeepingServiceItemListProps {
  item: CompletedWorkItem
}

const BookkeepingServiceItemList = ({
  item,
}: BookkeepingServiceItemListProps) => {
  return (
    <div className={styles.bookkeepingServiceItemList}>
      {item.name} x{item.quantity}
    </div>
  )
}
